import Box from '@mui/material/Box';
import { darken, type SxProps } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber';
import { useTranslation } from 'react-i18next';

// Hover border is a darker shade of the bar fill (see LalReachBarGauge)
const reachTypeHoverBorderColor = {
  Seed: darken(palette.charts.purple, 0.25),
  High: darken(palette.charts.blue, 0.25),
  Balanced: darken(palette.charts.lightBlue, 0.25),
  Scaled: darken(palette.charts.canary, 0.25),
};

export const LalReachBarGaugeItem = ({ sx, reach, reachType }: { sx?: SxProps; reach: number; reachType: string }) => {
  const { t } = useTranslation();

  const dataTest = `lal-reach-bar-gauge-item-${reachType}`.toLowerCase();
  const tooltipTitle =
    reachType === 'Seed' ? t('Seed Segment Reach') : t('{reachType} Similarity Reach', { reachType });

  return (
    <Tooltip
      placement="top"
      title={
        <Typography
          sx={{
            lineHeight: 1.25,
          }}
        >
          {tooltipTitle}:
          <Typography
            variant="body1Bold"
            component="span"
            sx={{
              display: 'block',
              lineHeight: 1.25,
            }}
          >
            {abbreviatedNumber(reach)}
          </Typography>
        </Typography>
      }
    >
      <Box
        tabIndex={0}
        sx={{
          position: 'absolute',
          insetBlock: 0,
          cursor: 'pointer',
          transition: 'z-index 50ms linear, box-shadow 50ms linear',
          border: '2px solid transparent',
          '&:hover': {
            zIndex: 4,
            boxShadow: ELEVATION.elevationGaugeHover,
            borderColor: reachTypeHoverBorderColor[reachType],
          },
          '&:focus-visible': {
            outline: 'none',
            zIndex: 4,
            boxShadow: ELEVATION.elevationGaugeHover,
            borderColor: reachTypeHoverBorderColor[reachType],
          },
          ...(sx ?? {}),
        }}
        aria-label={tooltipTitle}
        data-test={dataTest}
      />
    </Tooltip>
  );
};
